import React from 'react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import HeaderBanner from '../../../common/HeaderBanner';
import '../../../../styles/components/create-group.scss';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { withRouter } from '../../../../hoc/withRouter';
import { toast } from 'react-toastify';
import { getDeliverySite, updateDeliverySite } from './services/DeliverySiteService';
import {
  getAreasList,
  getMaterialsList,
  getUserList,
  getVehiclesList,
} from '../LoadingManagement/services/LoadingManagementService';

class DeliverySiteUpdate extends React.Component {
  constructor() {
    super();
    this.state = {
      // Basic Information
      delivery_schedule_id: '',
      sequence_number: '',
      project_name: '',
      lot_ref: '',
      area_id: '',
      auction_id: '',
      buyer_name: '',
      material_id: '',
      supervisor_id: '',
      expected_weight: '',
      load_from: '',
      load_to: '',
      expecting_start_date: '',
      expecting_end_date: '',
      notes: '',
      status: 'P',

      // Actual Delivery Information (to be filled later)
      actual_start_date: '',
      actual_end_date: '',
      actual_weight: '',

      // Dropdown data
      areas: [],
      auctions: [],
      buyers: [],
      materials: [],
      supervisors: [],
      vehicles: [],
      selectedVehicles: [],

      // Loading states
      isLoading: true,
      isSubmitting: false,
      errors: {},

      // UI states
      hideSidebar: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.loadInitialData();
  }

  async loadInitialData() {
    this.setState({ isLoading: true });

    try {
      // Load all dropdown data first
      await Promise.all([
        this.loadAreas(),
        this.loadMaterials(),
        this.loadVehicles(),
        this.loadSupervisors(),
      ]);

      // Then load the specific delivery site data
      const { params } = this.props;
      if (params?.id) {
        await this.loadDeliverySiteData(params.id);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load form data');
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async loadDeliverySiteData(deliverySiteId) {
    try {
      const response = await getDeliverySite(deliverySiteId);
      const data = response.data;

      // Extract vehicle IDs from vehicle_assignments
      const selectedVehicleIds =
        data.vehicle_assignments?.map((assignment) => assignment.vehicle_id) || [];

      this.setState({
        delivery_schedule_id: data.delivery_schedule_id || '', // ID not project name
        sequence_number: data.sequence_number,
        lot_ref: data.lot_ref,
        area_id: data.area_id,
        auction_id: data.auction_id,
        buyer_name: data.buyer_name,
        material_id: data.material_id,
        supervisor_id: data.supervisor_id,
        expected_weight: data.expected_weight,
        load_from: data.load_from,
        load_to: data.load_to,
        expecting_start_date: data.expecting_start_date
          ? data.expecting_start_date.split('T')[0]
          : '',
        expecting_end_date: data.expecting_end_date ? data.expecting_end_date.split('T')[0] : '',
        notes: data.notes || '',
        status: data.status,
        actual_start_date: data.actual_start_date ? data.actual_start_date.split('T')[0] : '',
        actual_end_date: data.actual_end_date ? data.actual_end_date.split('T')[0] : '',
        actual_weight: data.actual_weight || '',
        selectedVehicles: selectedVehicleIds,
      });
    } catch (error) {
      console.error('Error loading delivery site data:', error);
      toast.error('Failed to load delivery site details');
    }
  }

  async loadAreas() {
    try {
      const response = await getAreasList();
      this.setState({ areas: response.data || [] });
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }

  async loadMaterials() {
    try {
      const response = await getMaterialsList();
      this.setState({ materials: response.data || [] });
    } catch (error) {
      console.error('Error loading materials:', error);
    }
  }

  async loadVehicles() {
    try {
      const response = await getVehiclesList();
      this.setState({ vehicles: response.data || [] });
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  }

  async loadSupervisors() {
    try {
      const filters = { role: 'HSE' };
      const response = await getUserList(filters);
      this.setState({ supervisors: response.data || [] });
    } catch (error) {
      console.error('Error loading supervisors:', error);
    }
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });

    if (this.state.errors[name]) {
      this.setState((prevState) => ({
        errors: { ...prevState.errors, [name]: '' },
      }));
    }
  };

  handleVehicleSelection = (vehicleId) => {
    this.setState((prevState) => {
      const selectedVehicles = [...prevState.selectedVehicles];
      const index = selectedVehicles.indexOf(vehicleId);

      if (index > -1) {
        // Remove vehicle if already selected
        selectedVehicles.splice(index, 1);
      } else {
        // Add vehicle if not selected
        selectedVehicles.push(vehicleId);
      }

      return {
        selectedVehicles,
        errors: { ...prevState.errors, vehicles: '' },
      };
    });
  };

  validateForm = () => {
    const errors = {};

    // Required fields validation
    if (!this.state.delivery_schedule_id)
      errors.delivery_schedule_id = 'Delivery Schedule is required';
    if (!this.state.sequence_number) errors.sequence_number = 'Sequence Number is required';
    if (!this.state.lot_ref) errors.lot_ref = 'Lot Reference is required';
    if (!this.state.area_id) errors.area_id = 'Area is required';
    if (!this.state.auction_id) errors.auction_id = 'Auction is required';
    if (!this.state.buyer_name) errors.buyer_name = 'Buyer is required';
    if (!this.state.material_id) errors.material_id = 'Material is required';
    if (!this.state.supervisor_id) errors.supervisor_id = 'Supervisor is required';
    if (!this.state.expected_weight) errors.expected_weight = 'Expected Weight is required';
    if (!this.state.load_from) errors.load_from = 'Load From location is required';
    if (!this.state.load_to) errors.load_to = 'Load To location is required';
    if (!this.state.expecting_start_date)
      errors.expecting_start_date = 'Expected Start Date is required';
    if (!this.state.expecting_end_date) errors.expecting_end_date = 'Expected End Date is required';
    if (this.state.selectedVehicles.length === 0)
      errors.vehicles = 'At least one vehicle is required';

    // Date validation
    if (this.state.expecting_end_date && this.state.expecting_start_date) {
      if (new Date(this.state.expecting_end_date) < new Date(this.state.expecting_start_date)) {
        errors.expecting_end_date = 'Expected End Date must be after Expected Start Date';
      }
    }

    // Numeric validation
    if (this.state.expected_weight && parseFloat(this.state.expected_weight) < 0) {
      errors.expected_weight = 'Expected Weight must be greater than 0';
    }

    if (this.state.actual_weight && parseFloat(this.state.actual_weight) < 0) {
      errors.actual_weight = 'Actual Weight must be greater than 0';
    }

    // Actual dates validation (if provided)
    if (this.state.actual_end_date && this.state.actual_start_date) {
      if (new Date(this.state.actual_end_date) < new Date(this.state.actual_start_date)) {
        errors.actual_end_date = 'Actual End Date must be after Actual Start Date';
      }
    }

    this.setState({ errors });

    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const element =
        document.querySelector(`[name="${firstErrorField}"]`) ||
        document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    return true;
  };

  handleSubmit = async () => {
    if (!this.validateForm()) {
      return;
    }

    this.setState({ isSubmitting: true });

    try {
      // Create JSON payload
      const payload = {
        lot_ref: this.state.lot_ref,
        area_id: this.state.area_id,
        auction_id: this.state.auction_id,
        buyer_name: this.state.buyer_name,
        material_id: this.state.material_id,
        supervisor_id: this.state.supervisor_id,
        expected_weight: parseFloat(this.state.expected_weight),
        load_from: this.state.load_from,
        load_to: this.state.load_to,
        expecting_start_date: this.state.expecting_start_date,
        expecting_end_date: this.state.expecting_end_date,
        notes: this.state.notes || '',
        status: this.state.status,
        vehicle_ids: this.state.selectedVehicles,
      };

      // Add optional fields only if they have values
      if (this.state.actual_start_date) {
        payload.actual_start_date = this.state.actual_start_date;
      }
      if (this.state.actual_end_date) {
        payload.actual_end_date = this.state.actual_end_date;
      }
      if (this.state.actual_weight) {
        payload.actual_weight = parseFloat(this.state.actual_weight);
      }

      const response = await updateDeliverySite(this.props.params.id, payload);

      if (response.data && response.data.success) {
        toast.success('Delivery Site updated successfully!');
      } else {
        const errorMessage = response.data?.message || response.message || 'Unknown error';
        console.error('Update failed:', response.data);

        if (response.data?.errors) {
          const apiErrors = {};
          Object.keys(response.data.errors).forEach((key) => {
            apiErrors[key] = Array.isArray(response.data.errors[key])
              ? response.data.errors[key][0]
              : response.data.errors[key];
          });
          this.setState({ errors: apiErrors });
          toast.error('Please fix the validation errors');
        } else {
          toast.error('Error updating delivery site: ' + errorMessage);
        }
      }
    } catch (error) {
      console.error('Error updating delivery site:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Error updating delivery site: ' + (error.message || 'Unknown error'));
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  handleCancel = () => {
    this.props.navigate('/delivery-sites');
  };

  renderFieldError = (fieldName) => {
    if (this.state.errors[fieldName]) {
      return <div className="field-error">{this.state.errors[fieldName]}</div>;
    }
    return null;
  };

  renderLoadingSkeleton = () => {
    return (
      <div className="create-group-container">
        <div className="form-content">
          <div className="left-column">
            {/* Delivery Site Info Section Skeleton */}
            <div className="form-section">
              <div
                className="skeleton skeleton-title"
                style={{ width: '120px', marginBottom: '24px' }}
              ></div>
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="form-row">
                  <div className="form-field">
                    <div
                      className="skeleton skeleton-label"
                      style={{ width: '100px', marginBottom: '8px' }}
                    ></div>
                    <div className="skeleton skeleton-input"></div>
                  </div>
                  <div className="form-field">
                    <div
                      className="skeleton skeleton-label"
                      style={{ width: '80px', marginBottom: '8px' }}
                    ></div>
                    <div className="skeleton skeleton-input"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="right-column">
            {/* Actual Delivery Section Skeleton */}
            <div className="form-section">
              <div
                className="skeleton skeleton-title"
                style={{ width: '140px', marginBottom: '24px' }}
              ></div>
              {[1, 2, 3].map((item) => (
                <div key={item} className="form-field" style={{ marginBottom: '16px' }}>
                  <div
                    className="skeleton skeleton-label"
                    style={{ width: '120px', marginBottom: '8px' }}
                  ></div>
                  <div className="skeleton skeleton-input"></div>
                </div>
              ))}
            </div>

            {/* Vehicles Section Skeleton */}
            <div className="form-section">
              <div
                className="skeleton skeleton-title"
                style={{ width: '100px', marginBottom: '24px' }}
              ></div>
              <div className="skeleton skeleton-checkbox-group" style={{ height: '150px' }}></div>
            </div>
          </div>
        </div>

        <div className="action-buttons-l2 border-top-light">
          <div
            className="skeleton skeleton-button"
            style={{ width: '100px', height: '44px' }}
          ></div>
          <div
            className="skeleton skeleton-button"
            style={{ width: '140px', height: '44px' }}
          ></div>
        </div>
      </div>
    );
  };

  render() {
    const { areas, materials, vehicles, supervisors, buyers, isLoading } = this.state;

    const isArabic = i18n.language === 'ar';

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('edit_delivery_site')}
          subtitle={i18n.t('edit_delivery_site_subtitle')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div
                className={
                  this.state.hideSidebar
                    ? 'col-xl-12 col-lg-12 col-md-12'
                    : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !this.state.hideSidebar })}
                      >
                        {this.state.hideSidebar && <i className="icon-size-actual"></i>}
                        {!this.state.hideSidebar && <i className="icon-size-fullscreen"></i>}
                      </span>
                    </div>
                    <div className="header-action">
                      <button
                        className="button1 button1-gradient button1-back"
                        onClick={() => this.props.navigate('/delivery-site-list')}
                        disabled={isLoading}
                      >
                        <ArrowLeft size={18} className="icon" />
                        <span>{i18n.t('back')}</span>
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading ? (
                      this.renderLoadingSkeleton()
                    ) : (
                      <div className="create-group-container">
                        <div className="form-content">
                          <div className="left-column">
                            <div className="form-section">
                              <h2>{i18n.t('delivery_site_info')}</h2>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>{i18n.t('delivery_schedule')} *</label>
                                  <input
                                    type="text"
                                    name="delivery_schedule_id"
                                    value={this.state.delivery_schedule_id}
                                    onChange={this.handleInputChange}
                                    placeholder="Enter delivery schedule ID"
                                    className={
                                      this.state.errors.delivery_schedule_id ? 'error' : ''
                                    }
                                  />
                                  {this.renderFieldError('delivery_schedule_id')}
                                </div>
                                <div className="form-field">
                                  <label>{i18n.t('sequence_number')} *</label>
                                  <input
                                    type="number"
                                    name="sequence_number"
                                    value={this.state.sequence_number}
                                    onChange={this.handleInputChange}
                                    placeholder="e.g., 1"
                                    min="1"
                                    className={this.state.errors.sequence_number ? 'error' : ''}
                                  />
                                  {this.renderFieldError('sequence_number')}
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>{i18n.t('lot_reference')} *</label>
                                  <input
                                    type="text"
                                    name="lot_ref"
                                    value={this.state.lot_ref}
                                    onChange={this.handleInputChange}
                                    placeholder="e.g., MZADLOT_2"
                                    className={this.state.errors.lot_ref ? 'error' : ''}
                                  />
                                  {this.renderFieldError('lot_ref')}
                                </div>
                                <div className="form-field">
                                  <label>{i18n.t('area')} *</label>
                                  <select
                                    name="area_id"
                                    value={this.state.area_id}
                                    onChange={this.handleInputChange}
                                    className={this.state.errors.area_id ? 'error' : ''}
                                  >
                                    <option value="">{i18n.t('select_area')}</option>
                                    {areas.map((area) => (
                                      <option key={area.id} value={area.id}>
                                        {area.place_name}
                                      </option>
                                    ))}
                                  </select>
                                  {this.renderFieldError('area_id')}
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>{i18n.t('auction')} *</label>
                                  <input
                                    type="text"
                                    name="auction_id"
                                    value={this.state.auction_id}
                                    onChange={this.handleInputChange}
                                    placeholder="Enter auction ID"
                                    className={this.state.errors.auction_id ? 'error' : ''}
                                  />
                                  {this.renderFieldError('auction_id')}
                                </div>

                                <div className="form-field">
                                  <label>{i18n.t('buyer')} *</label>
                                  <input
                                    type="text"
                                    name="buyer_name"
                                    value={this.state.buyer_name}
                                    onChange={this.handleInputChange}
                                    placeholder="Enter auction ID"
                                    className={this.state.errors.buyer_name ? 'error' : ''}
                                  />
                                  {this.renderFieldError('buyer_name')}
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>{i18n.t('material')} *</label>
                                  <select
                                    name="material_id"
                                    value={this.state.material_id}
                                    onChange={this.handleInputChange}
                                    className={this.state.errors.material_id ? 'error' : ''}
                                  >
                                    <option value="">{i18n.t('select_material')}</option>
                                    {materials.map((material) => (
                                      <option key={material.id} value={material.id}>
                                        {material.name}
                                      </option>
                                    ))}
                                  </select>
                                  {this.renderFieldError('material_id')}
                                </div>
                                <div className="form-field">
                                  <label>{i18n.t('supervisor')} *</label>
                                  <select
                                    name="supervisor_id"
                                    value={this.state.supervisor_id}
                                    onChange={this.handleInputChange}
                                    className={this.state.errors.supervisor_id ? 'error' : ''}
                                  >
                                    <option value="">{i18n.t('select_supervisor')}</option>
                                    {supervisors.map((supervisor) => (
                                      <option key={supervisor.id} value={supervisor.id}>
                                        {supervisor.name}
                                      </option>
                                    ))}
                                  </select>
                                  {this.renderFieldError('supervisor_id')}
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>{i18n.t('expected_weight')} (kg) *</label>
                                  <input
                                    type="number"
                                    name="expected_weight"
                                    value={this.state.expected_weight}
                                    onChange={this.handleInputChange}
                                    placeholder="e.g., 10000"
                                    step="0.01"
                                    min="0"
                                    className={this.state.errors.expected_weight ? 'error' : ''}
                                  />
                                  {this.renderFieldError('expected_weight')}
                                </div>
                                <div className="form-field">
                                  <label>{i18n.t('status')}</label>
                                  <select
                                    name="status"
                                    value={this.state.status}
                                    onChange={this.handleInputChange}
                                  >
                                    <option value="P">{i18n.t('pending')}</option>
                                    <option value="I">{i18n.t('in_transit')}</option>
                                    <option value="C">{i18n.t('completed')}</option>
                                    <option value="X">{i18n.t('cancelled')}</option>
                                  </select>
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>{i18n.t('load_from')} *</label>
                                  <input
                                    type="text"
                                    name="load_from"
                                    value={this.state.load_from}
                                    onChange={this.handleInputChange}
                                    placeholder="e.g., DUBAI"
                                    className={this.state.errors.load_from ? 'error' : ''}
                                  />
                                  {this.renderFieldError('load_from')}
                                </div>
                                <div className="form-field">
                                  <label>{i18n.t('load_to')} *</label>
                                  <input
                                    type="text"
                                    name="load_to"
                                    value={this.state.load_to}
                                    onChange={this.handleInputChange}
                                    placeholder="e.g., ABU DHABI"
                                    className={this.state.errors.load_to ? 'error' : ''}
                                  />
                                  {this.renderFieldError('load_to')}
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-field">
                                  <label>{i18n.t('expecting_start_date')} *</label>
                                  <input
                                    type="date"
                                    name="expecting_start_date"
                                    value={this.state.expecting_start_date}
                                    onChange={this.handleInputChange}
                                    className={
                                      this.state.errors.expecting_start_date ? 'error' : ''
                                    }
                                  />
                                  {this.renderFieldError('expecting_start_date')}
                                </div>
                                <div className="form-field">
                                  <label>{i18n.t('expecting_end_date')} *</label>
                                  <input
                                    type="date"
                                    name="expecting_end_date"
                                    value={this.state.expecting_end_date}
                                    onChange={this.handleInputChange}
                                    className={this.state.errors.expecting_end_date ? 'error' : ''}
                                  />
                                  {this.renderFieldError('expecting_end_date')}
                                </div>
                              </div>

                              <div className="form-field full-width">
                                <label>{i18n.t('notes')}</label>
                                <textarea
                                  name="notes"
                                  value={this.state.notes}
                                  onChange={this.handleInputChange}
                                  placeholder="Enter any additional notes..."
                                  rows="3"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="right-column">
                            <div className="form-section">
                              <h2>{i18n.t('actual_delivery_info')}</h2>

                              <div className="form-field">
                                <label>{i18n.t('actual_start_date')}</label>
                                <input
                                  type="date"
                                  name="actual_start_date"
                                  value={this.state.actual_start_date}
                                  onChange={this.handleInputChange}
                                  className={this.state.errors.actual_start_date ? 'error' : ''}
                                />
                                {this.renderFieldError('actual_start_date')}
                              </div>

                              <div className="form-field">
                                <label>{i18n.t('actual_end_date')}</label>
                                <input
                                  type="date"
                                  name="actual_end_date"
                                  value={this.state.actual_end_date}
                                  onChange={this.handleInputChange}
                                  className={this.state.errors.actual_end_date ? 'error' : ''}
                                />
                                {this.renderFieldError('actual_end_date')}
                              </div>

                              <div className="form-field">
                                <label>{i18n.t('actual_weight')} (kg)</label>
                                <input
                                  type="number"
                                  name="actual_weight"
                                  value={this.state.actual_weight}
                                  onChange={this.handleInputChange}
                                  placeholder="e.g., 9800"
                                  step="0.01"
                                  min="0"
                                  className={this.state.errors.actual_weight ? 'error' : ''}
                                />
                                {this.renderFieldError('actual_weight')}
                              </div>
                            </div>

                            <div className="form-section">
                              <h2>{i18n.t('assigned_vehicles')} *</h2>
                              <div className="vehicles-toggle-group">
                                {vehicles.map((vehicle) => (
                                  <div className="vehicle-toggle-item" key={vehicle.id}>
                                    <div className="toggle-field">
                                      <div className="vehicle-info">
                                        <label>
                                          {vehicle.vehicle_number} - {vehicle.vehicle_model}
                                        </label>
                                      </div>
                                      <div
                                        className={`toggle ${this.state.selectedVehicles.includes(vehicle.id) ? 'active' : ''}`}
                                        onClick={() => this.handleVehicleSelection(vehicle.id)}
                                      >
                                        <div className="toggle-slider"></div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {this.renderFieldError('vehicles')}
                            </div>
                          </div>
                        </div>

                        <div className="action-buttons-l2 border-top-light">
                          <button
                            className="button1 button1-gray"
                            onClick={this.handleCancel}
                            disabled={this.state.isSubmitting}
                          >
                            {i18n.t('cancel')}
                          </button>
                          <button
                            className="button1 button1-primary"
                            onClick={this.handleSubmit}
                            disabled={this.state.isSubmitting}
                          >
                            {this.state.isSubmitting ? (
                              <>
                                <Loader2 className="animate-spin" size={18} />
                                <span>{i18n.t('update_delivery_site')}</span>
                              </>
                            ) : (
                              <span>{i18n.t('update_delivery_site')}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default withRouter(DeliverySiteUpdate);
